import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, UploadSimple, Trash, Download, MagnifyingGlass, PencilSimple, ArrowsClockwise, Eye, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import { useConfirm } from "@/components/ConfirmDialog";
import { DatePicker } from "@/components/ui/date-picker";
import { DOCUMENT_CATEGORIES } from "@/lib/constants";

const CATEGORIES = [...new Set(["Objektakte", "Eigentümer", "Mieter", "Mietverhältnis", "Schriftverkehr", "Rechnungen", "Verträge", "Betriebskosten", "Nebenkosten", "Tickets", "Versicherungen", "Bank", ...DOCUMENT_CATEGORIES, "Sonstiges"])];

export default function Documents() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  // Cache der verknüpfbaren Entitäten (lazy geladen)
  const [linkData, setLinkData] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const params = {};
    if (category !== "all") params.category = category;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/documents", { params });
    setItems(data);
  };

  const deleteDoc = async (d) => {
    const ok = await confirm({
      title: "Dokument löschen?",
      description: `Das Dokument "${d.title}" wird endgültig gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300); // debounce
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [category, q]);

  const ensureLinkData = async () => {
    if (linkData) return linkData;
    const [props, units, tens, contacts] = await Promise.all([
      api.get("/properties").then((r) => r.data).catch(() => []),
      api.get("/properties/units/all").then((r) => r.data).catch(() => []),
      api.get("/tenancies").then((r) => r.data).catch(() => []),
      api.get("/contacts", { params: { limit: 1000 } }).then((r) => r.data).catch(() => []),
    ]);
    const d = { properties: props, units, tenancies: tens, contacts };
    setLinkData(d);
    return d;
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "dokument";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const { sorted, SortableTh } = useTableSort(items, "created_at", "desc", {
    title: (d) => d.title || "",
    category: (d) => d.category || "",
    direction: (d) => d.direction || "",
    filename: (d) => d.original_filename || "",
    size: (d) => Number(d.size) || 0,
    created_at: (d) => d.created_at || "",
  });

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dokumente</h1>
          <p className="text-sm text-slate-500 mt-1">Dokumentenablage mit Zuordnung</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="upload-doc-btn" onClick={ensureLinkData}>
              <UploadSimple size={16} /> Datei hochladen
            </Button>
          </DialogTrigger>
          <UploadDialog onSaved={() => { setOpen(false); load(); }} linkData={linkData} />
        </Dialog>
      </div>

      {/* Filterleiste */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Suchen nach Titel, Dateiname, Beschreibung, Verfasser, Zeichen…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="doc-search"
          />
        </div>
        <Label className="text-xs text-slate-600">Kategorie:</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-52 h-9" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">{items.length} Dokument(e)</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Mobile-Karten */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="documents-mobile-list">
          {sorted.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">
              <FileText size={32} className="mx-auto mb-2 text-slate-300" />
              {q ? "Keine Treffer für die Suche." : "Noch keine Dokumente."}
            </div>
          )}
          {sorted.map((d) => (
            <div key={d.id} className="p-3" data-testid={`doc-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-medium text-slate-900 text-sm truncate flex items-center gap-1">
                  {d.title}
                  {d.voided && <span className="text-[10px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5" title={d.void_reason} data-testid={`doc-voided-badge-${d.id}`}>storniert / gegenstandslos</span>}
                  {d.attachments_count > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] font-normal text-teal-800 bg-teal-100 border border-teal-200 rounded-full px-1.5 py-0.5"
                      title={`${d.attachments_count} Anlage${d.attachments_count === 1 ? '' : 'n'} verknüpft`}
                      data-testid={`doc-attachments-badge-${d.id}`}
                    >
                      <Paperclip size={9} weight="bold" /> {d.attachments_count}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                  d.direction === "in" ? "bg-blue-100 text-blue-800" :
                  d.direction === "out" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                }`}>
                  {d.direction === "in" ? "Eingehend" : d.direction === "out" ? "Ausgehend" : "Intern"}
                </span>
              </div>
              <div className="text-xs text-slate-600">{d.category}</div>
              <div className="text-xs text-slate-500 truncate">{d.original_filename}{d.version > 1 ? ` · v${d.version}` : ""}</div>
              {(d.author_name || d.author_code) && (
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Verfasser: {d.author_name || "—"}{d.author_code ? ` · ${d.author_code}` : ""}
                </div>
              )}
              {d.notes && <div className="text-[11px] text-slate-600 mt-1 italic line-clamp-2">{d.notes}</div>}
              <div className="flex items-center justify-between mt-2">
                <div className="text-[11px] text-slate-500 tabular-nums">
                  {formatDate(d.document_date || d.created_at)} · {d.size ? `${Math.round(d.size / 1024)} KB` : ""}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setPreviewDoc(d)} data-testid={`doc-view-mobile-${d.id}`} title="Vorschau">
                    <Eye size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={async () => { await ensureLinkData(); setEditing(d); }} data-testid={`doc-edit-mobile-${d.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(d)}>
                    <Download size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700"
                    onClick={() => deleteDoc(d)}
                    data-testid={`doc-del-mobile-${d.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <SortableTh field="title">Titel</SortableTh>
              <SortableTh field="category">Kategorie</SortableTh>
              <SortableTh field="direction">Richtung</SortableTh>
              <SortableTh field="filename">Datei</SortableTh>
              <SortableTh field="size" align="right" className="text-right">Größe</SortableTh>
              <SortableTh field="created_at">Hochgeladen</SortableTh>
              <Th className="w-28 text-right"></Th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">
              <FileText size={32} className="mx-auto mb-2 text-slate-300" />
              {q ? "Keine Treffer für die Suche." : "Noch keine Dokumente."}
            </td></tr>}
            {sorted.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                <Td className="font-medium text-slate-900">
                  <div className="flex items-center gap-1.5">
                    <span>{d.title}</span>
                    {d.voided && <span className="text-[10px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5" title={d.void_reason} data-testid={`doc-voided-row-badge-${d.id}`}>storniert / gegenstandslos</span>}
                    {d.attachments_count > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-normal text-teal-800 bg-teal-100 border border-teal-200 rounded-full px-1.5 py-0.5"
                        title={`${d.attachments_count} Anlage${d.attachments_count === 1 ? '' : 'n'} verknüpft`}
                        data-testid={`doc-table-attach-${d.id}`}
                      >
                        <Paperclip size={9} weight="bold" /> {d.attachments_count}
                      </span>
                    )}
                  </div>
                  {(d.author_name || d.author_code) && (
                    <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`doc-author-${d.id}`}>
                      Verfasser: {d.author_name || "—"}{d.author_code ? ` · ${d.author_code}` : ""}
                      {d.their_reference ? ` · Ihr Zeichen: ${d.their_reference}` : ""}
                    </div>
                  )}
                  {d.notes && <div className="text-[11px] text-slate-600 italic mt-0.5 line-clamp-1">{d.notes}</div>}
                </Td>
                <Td>{d.category}</Td>
                <Td>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    d.direction === "in" ? "bg-blue-100 text-blue-800" :
                    d.direction === "out" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                  }`}>
                    {d.direction === "in" ? "Eingehend" : d.direction === "out" ? "Ausgehend" : "Intern"}
                  </span>
                </Td>
                <Td className="text-slate-600 text-xs">
                  <div className="truncate max-w-[220px]">{d.original_filename}</div>
                  {d.version > 1 && <div className="text-[10px] text-slate-400">Version {d.version}</div>}
                </Td>
                <Td className="text-right tabular-nums text-xs">{d.size ? `${Math.round(d.size / 1024)} KB` : "—"}</Td>
                <Td className="tabular-nums text-xs">{formatDate(d.document_date || d.created_at)}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setPreviewDoc(d)} data-testid={`doc-view-${d.id}`} title="Vorschau">
                      <Eye size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={async () => { await ensureLinkData(); setEditing(d); }} data-testid={`doc-edit-${d.id}`} title="Bearbeiten">
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(d)} data-testid={`doc-dl-${d.id}`} title="Herunterladen">
                      <Download size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700"
                      onClick={() => deleteDoc(d)}
                      data-testid={`doc-del-${d.id}`}
                      title="Löschen"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        {editing && (
          <EditDocumentDialog
            doc={editing}
            linkData={linkData}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>

      {previewDoc && (
        <DocumentPreviewDrawer
          docId={previewDoc.id}
          metaOverride={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}

function LinkedEntities({ form, set, linkData }) {
  const props = linkData?.properties || [];
  const units = linkData?.units || [];
  const tens = linkData?.tenancies || [];
  const contacts = linkData?.contacts || [];

  const propLabel = (p) => `${p.property_number || ""} ${p.name || `${p.address?.street || ""} ${p.address?.house_number || ""}`}`.trim();
  const unitLabel = (u) => `${u.unit_number || u.id?.slice(0, 6)}${u.location ? ` · ${u.location}` : ""}`;
  const tenLabel = (t) => `${t.contract_number || ""} · ${t.tenant_names || ""}`.trim();
  const contactLabel = (c) =>
    c.contact_type === "company" || c.contact_type === "institution"
      ? c.company_name || "—"
      : `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "—";

  // Filter units to only those of the selected property
  const unitsForProp = form.linked_property_id ? units.filter((u) => u.property_id === form.linked_property_id) : units;
  // Filter tenancies to only those of selected property/unit
  const tensForFilter = tens.filter((t) =>
    (!form.linked_property_id || t.property_id === form.linked_property_id) &&
    (!form.linked_unit_id || t.unit_id === form.linked_unit_id)
  );

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-2.5" data-testid="doc-linked-entities">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Zuordnung (optional)</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Objekt</Label>
          <Select value={form.linked_property_id || "none"} onValueChange={(v) => set("linked_property_id", v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="doc-link-property"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Kein Objekt —</SelectItem>
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{propLabel(p)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Einheit</Label>
          <Select value={form.linked_unit_id || "none"} onValueChange={(v) => set("linked_unit_id", v === "none" ? "" : v)} disabled={!form.linked_property_id && units.length > 50}>
            <SelectTrigger className="mt-1" data-testid="doc-link-unit"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Keine Einheit —</SelectItem>
              {unitsForProp.map((u) => <SelectItem key={u.id} value={u.id}>{unitLabel(u)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Mietverhältnis</Label>
          <Select value={form.linked_tenancy_id || "none"} onValueChange={(v) => set("linked_tenancy_id", v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="doc-link-tenancy"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Kein Mietverhältnis —</SelectItem>
              {tensForFilter.map((t) => <SelectItem key={t.id} value={t.id}>{tenLabel(t)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Kontakt</Label>
          <Select value={form.linked_contact_id || "none"} onValueChange={(v) => set("linked_contact_id", v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="doc-link-contact"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">— Kein Kontakt —</SelectItem>
              {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{contactLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function UploadDialog({ onSaved, linkData }) {
  const fileRef = useRef();
  const [form, setForm] = useState({
    title: "", category: "Sonstiges", direction: "internal", notes: "",
    linked_property_id: "", linked_unit_id: "", linked_tenancy_id: "", linked_contact_id: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Bitte Datei wählen"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title || file.name);
    fd.append("category", form.category);
    fd.append("direction", form.direction);
    fd.append("notes", form.notes);
    if (form.linked_property_id) fd.append("linked_property_id", form.linked_property_id);
    if (form.linked_unit_id) fd.append("linked_unit_id", form.linked_unit_id);
    if (form.linked_tenancy_id) fd.append("linked_tenancy_id", form.linked_tenancy_id);
    if (form.linked_contact_id) fd.append("linked_contact_id", form.linked_contact_id);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument hochgeladen");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Datei hochladen</DialogTitle>
        <DialogDescription>Datei + Metadaten + optionale Zuordnung zu Objekt/Einheit/Mietverhältnis/Kontakt.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Datei (max 25 MB)">
          <Input type="file" ref={fileRef} data-testid="doc-file-input" />
        </F>
        <F label="Titel"><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Optional, sonst Dateiname" data-testid="doc-title-input" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Richtung">
            <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
              <SelectTrigger data-testid="doc-direction-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Eingehend</SelectItem>
                <SelectItem value="out">Ausgehend</SelectItem>
                <SelectItem value="internal">Intern</SelectItem>
              </SelectContent>
            </Select>
          </F>
        </div>

        <LinkedEntities form={form} set={set} linkData={linkData} />

        <F label="Beschreibung (optional)">
          <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="z. B. Wofür wird das Dokument verwendet? Notizen…" data-testid="doc-notes-input" />
        </F>

        <DialogFooter>
          <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-upload-submit">
            {loading ? "Lade hoch..." : "Hochladen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditDocumentDialog({ doc, linkData, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    document_date: (doc.document_date || doc.created_at || "").slice(0, 10),
    category: doc.category || "Sonstiges",
    direction: doc.direction || "internal",
    notes: doc.notes || "",
    linked_property_id: doc.linked_property_id || "",
    linked_unit_id: doc.linked_unit_id || "",
    linked_tenancy_id: doc.linked_tenancy_id || "",
    linked_contact_id: doc.linked_contact_id || "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const newFileRef = useRef();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/documents/${doc.id}`, form);
      toast.success("Dokument aktualisiert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const uploadNewVersion = async () => {
    const file = newFileRef.current?.files?.[0];
    if (!file) { toast.error("Bitte neue Datei wählen"); return; }
    setUploadingVersion(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documents/${doc.id}/new-version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Neue Version hochgeladen");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versions-Upload fehlgeschlagen");
    } finally {
      setUploadingVersion(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-edit-dialog">
      <DialogHeader>
        <DialogTitle>Dokument bearbeiten</DialogTitle>
        <DialogDescription>Metadaten anpassen oder eine neue Version der Datei hochladen.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
          <span className="font-semibold text-slate-700">Aktuelle Datei:</span> {doc.original_filename || "—"}
          {doc.version > 1 && <span className="ml-2 font-mono text-slate-500">(Version {doc.version})</span>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Titel"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="doc-edit-title" /></F>
          <F label="Dokumentdatum"><DatePicker value={form.document_date} onChange={(v) => set("document_date", v)} data-testid="doc-edit-date" /></F>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="doc-edit-category"><SelectValue /></SelectTrigger>
              <SelectContent>{[...new Set([...CATEGORIES, form.category])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Richtung">
            <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
              <SelectTrigger data-testid="doc-edit-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Eingehend</SelectItem>
                <SelectItem value="out">Ausgehend</SelectItem>
                <SelectItem value="internal">Intern</SelectItem>
              </SelectContent>
            </Select>
          </F>
        </div>

        <LinkedEntities form={form} set={set} linkData={linkData} />

        <F label="Beschreibung">
          <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="doc-edit-notes" />
        </F>

        <div className="border border-slate-200 rounded-md p-3 bg-amber-50/40 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold flex items-center gap-1">
            <ArrowsClockwise size={12} /> Neue Datei-Version hochladen (optional)
          </div>
          <div className="text-[11px] text-slate-600">
            Die alte Version wird im Versionsverlauf archiviert (nicht gelöscht). Die Metadaten bleiben unverändert.
          </div>
          <div className="flex items-center gap-2">
            <Input type="file" ref={newFileRef} className="flex-1" data-testid="doc-edit-new-file" />
            <Button type="button" variant="outline" disabled={uploadingVersion} onClick={uploadNewVersion} data-testid="doc-edit-new-version-btn">
              {uploadingVersion ? "Lade hoch…" : "Version ersetzen"}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-edit-save-btn">
            {loading ? "Speichere..." : "Metadaten speichern"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
